import type { MapLibreGlDirectionsConfiguration } from "@maplibre/maplibre-gl-directions"
import MapLibreGlDirections, {
  Directions,
  LineString,
  MapLibreGlDirectionsRoutingEvent,
  MapLibreGlDirectionsWaypointEvent,
} from "@maplibre/maplibre-gl-directions"
import type {
  ILngLatLike,
  Map,
  MapGeoJSONFeature,
  MapMouseEvent,
  MapTouchEvent,
} from "maplibre-gl"
import type { Feature, Point } from "geojson"
import * as polyline from "@mapbox/polyline"
import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { debounce } from "lodash"
import { stopPinLabels } from "@/lib/stopNumbers"
import { TrafficSection } from "./RouteView"
import {
  TrafficRange,
  congestionFromLegTraffic,
  legBoundaries,
  splitCongestionByLeg,
} from "./routeTraffic"
import {
  RouteDragKind,
  RouteDragSummary,
  trackRouteEvent,
} from "./routeTelemetry"

export interface RouteLineDragEndEventDetail {
  waypointIndex: number
  waypoint: [number, number]
}

export interface RouteLineDragEndEvent extends CustomEvent {
  detail: RouteLineDragEndEventDetail
}

declare global {
  interface HTMLElementEventMap {
    routelinedragend: RouteLineDragEndEvent
  }
}

// Array of polylines, where each polyline is an array of [lng, lat] coordinates
export type PolylineArray = string[]

// Callback function type that receives waypoints and returns polylines
export type RouteCallback = (
  waypoints: [number, number][],
  stops?: RouteStop[],
) => Promise<PolylineArray> | PolylineArray

export default class CustomMapLibreGlDirections extends MapLibreGlDirections {
  private routeCallback: RouteCallback | null = null
  private isDraggingRouteLine = false
  private waypointsBeforeDrag: [number, number][] = []
  private stops: RouteStop[] | undefined = undefined
  private routeJid: string | null = null
  private debouncedFetchDirections: {
    (event: MapLibreGlDirectionsWaypointEvent): void
    cancel?: () => void
  }
  private showCurrentLocation: boolean = true
  private onRouteDragChange: ((isDragging: boolean) => void) | null = null
  // Telemetry state: what the stored route looked like when drawn, and the
  // last pointer drag on a route feature
  private storedRouteWaypoints: [number, number][] | null = null
  private storedRouteDrawnAt: number | null = null
  private activeDrag: {
    kind: RouteDragKind
    startPoint: { x: number; y: number }
  } | null = null
  private lastDrag: RouteDragSummary | null = null
  // Waypoint count when the (last) debounced fetch was queued; the request
  // itself reads the waypoints 300 ms later
  private waypointCountWhenQueued: number | null = null
  // The stored (saved) route is authoritative: bumped whenever it is drawn or
  // cleared so pending ad-hoc responses for older waypoints are dropped
  private routeGeneration = 0
  private storedSnappoints: Feature<Point>[] | null = null
  private storedRoutelines: Feature<LineString>[][] | null = null

  constructor(
    map: Map,
    configuration?: Partial<MapLibreGlDirectionsConfiguration> & {
      showCurrentLocation: boolean
      stops: RouteStop[] | undefined
    },
  ) {
    super(map, configuration)
    this.showCurrentLocation = !!configuration?.showCurrentLocation
    this.stops = configuration?.stops
    this.debouncedFetchDirections = debounce(
      this.trackedFetchDirections.bind(this),
      300, // 300ms delay
      { leading: false, trailing: true },
    )
  }

  setStops(stops: RouteStop[]) {
    this.stops = stops
  }

  setRouteJid(routeJid: string | null) {
    this.routeJid = routeJid
  }

  setShowCurrentLocation(value: boolean) {
    this.showCurrentLocation = value
  }

  /**
   * Set a callback to be notified when route line drag state changes.
   * This is useful for hiding alternative routes during dragging.
   *
   * @param callback - Function called with true when drag starts, false when drag ends
   */
  setOnRouteDragChange(callback: ((isDragging: boolean) => void) | null): void {
    this.onRouteDragChange = callback
  }

  /**
   * Get the current dragging state
   */
  getIsDraggingRouteLine(): boolean {
    return this.isDraggingRouteLine
  }

  /**
   * Set a custom callback to be used instead of the default API requests.
   * The callback should take an array of waypoint coordinates and return an array of polylines.
   *
   * @param callback - Function that takes waypoints and returns polylines
   */
  setRouteCallback(callback: RouteCallback | null): void {
    this.routeCallback = callback
  }

  // Method to transform TomTom traffic data into congestion values
  processTomTomTraffic(
    sections: TrafficSection[],
    waypoints: [number, number][],
  ) {
    // Create a congestion array that matches the route points
    const congestionValues: Array<number | null> = new Array(
      waypoints.length,
    ).fill(null)

    // Apply traffic sections to the congestion array
    sections.forEach((section) => {
      // Map magnitude of delay to congestion value
      let congestionValue
      if (section.simpleCategory === "JAM") {
        congestionValue = 100 // Max congestion for jams
      } else if (section.magnitudeOfDelay === 1) {
        congestionValue = 80 // High congestion for severe delays
      } else if (section.magnitudeOfDelay === 4) {
        congestionValue = 20 // Low congestion for minor delays
      } else {
        // Calculate congestion based on speed (lower speed = higher congestion)
        // You may need to adjust these values based on road type
        const freeflowSpeed = 130 // Assumed free-flow speed in km/h
        const speedRatio = Math.min(
          1,
          section.effectiveSpeedInKmh / freeflowSpeed,
        )
        congestionValue = Math.round((1 - speedRatio) * 90) // Scale to 0-90
      }

      // Apply congestion value to all points in this section
      for (let i = section.startPointIndex; i <= section.endPointIndex; i++) {
        congestionValues[i] = congestionValue
      }
    })

    return congestionValues
  }

  protected async actualFetchDirections(
    originalEvent: MapLibreGlDirectionsWaypointEvent,
  ) {
    if (!this.routeCallback || !this.map.isStyleLoaded()) return
    if (this._waypoints.length >= 2) {
      this.fire(
        new MapLibreGlDirectionsRoutingEvent("fetchroutesstart", originalEvent),
      )

      if (!this.profiles.length) {
        this.profiles = [this.configuration.profile]
      }

      // Profiles for requests
      const profiles: string[] = []
      // Waypoints split by profile
      const waypoints: Feature<Point>[][] = []

      /**
       * Prepares data for the requests
       */
      this.profiles.reduce((waypointsIndex, profile, index) => {
        const isLast = index === this.profiles.length - 1
        const prevProfile = index > 0 ? this.profiles[index - 1] : undefined
        const isSameProfile = profile === prevProfile
        const waypointsEnd = isLast
          ? /**
             * If it's the last supplied profile include all remaining waypoints
             */
            this._waypoints.length
          : isSameProfile
            ? /**
               * If profile is same as previous one add a slice of one element only
               */
              waypointsIndex + 1
            : /**
               * If profile is different slice corresponding pair of coordinates
               */
              waypointsIndex + 2

        if (isSameProfile) {
          /**
           * If route to the next waypoint is to be found with the same profile, add waypoints to the previous chunk to
           * fetch them in one request
           */
          waypoints[waypoints.length - 1].push(
            ...this._waypoints.slice(waypointsIndex, waypointsEnd),
          )
        } else {
          /**
           * Otherwise add waypoints as the next chunk and push new profile
           */
          waypoints.push(this._waypoints.slice(waypointsIndex, waypointsEnd))
          profiles.push(profile)
        }

        return waypointsEnd
      }, 0)

      const requests = profiles.map((profile, index) => {
        return this.buildRequest(
          { ...this.configuration, profile },
          this.getWaypointsCoordinates(waypoints[index]),
          this.configuration.bearings
            ? this.getWaypointsBearings(waypoints[index])
            : undefined,
        )
      })
      const generation = this.routeGeneration
      const responses = await Promise.all(
        requests.map(async () => {
          let response: Directions | undefined = undefined
          try {
            const newIndex = this.findNewWaypointIndex(
              this.waypointsBeforeDrag,
              this.waypointsCoordinates,
            )
            const changedIndex = this.findChangedWaypointIndex(
              this.waypointsBeforeDrag,
              this.waypointsCoordinates,
            )

            let stopsForCallback = this.stops ?? []

            const offset = this.routeJid === "on-page" ? 0 : 1

            if (newIndex !== -1) {
              // Adding a new passthrough waypoint (dragging route line)
              const newPassthroughStop: RouteStop = {
                addressUpdated: true,
                action: "passthrough",
                location: {
                  formattedAddress: `${this.waypointsCoordinates[newIndex][1]},${this.waypointsCoordinates[newIndex][0]}`,
                },
              }
              console.log(
                "[DEBUG] Calling route callback with NEW waypoint:",
                this.waypointsBeforeDrag,
                this.waypointsCoordinates,
                this.stops,
                newIndex - offset,
                this.routeJid,
              )
              stopsForCallback = [...(this.stops ?? [])]
              stopsForCallback.splice(newIndex - offset, 0, newPassthroughStop)
            } else if (changedIndex !== -1) {
              // Moving existing waypoint - use current stops as-is
              stopsForCallback = [...(this.stops ?? [])]
              stopsForCallback[changedIndex - offset] = {
                ...stopsForCallback[changedIndex - offset],
                addressUpdated: true,
                route_handle: undefined,
                location: {
                  formattedAddress: `${this.waypointsCoordinates[changedIndex][1]},${this.waypointsCoordinates[changedIndex][0]}`,
                },
              }
              if (changedIndex < this.waypointsCoordinates.length - 1) {
                stopsForCallback[changedIndex + 1 - offset] = {
                  ...stopsForCallback[changedIndex + 1 - offset],
                  addressUpdated: true,
                  route_handle: undefined,
                  location: {
                    formattedAddress: `${this.waypointsCoordinates[changedIndex + 1 - offset][1]},${this.waypointsCoordinates[changedIndex + 1 - offset][0]}`,
                  },
                }
              }
            }

            const polylines = await this.routeCallback!(
              this.waypointsCoordinates,
              stopsForCallback,
            )

            // Return early if the polylines array is empty, or if the stored
            // route was drawn or cleared while this request was in flight
            if (!polylines.length || generation !== this.routeGeneration) {
              this.fire(
                new MapLibreGlDirectionsRoutingEvent(
                  "fetchroutesend",
                  originalEvent,
                  undefined,
                ),
              )
              return undefined
            }

            const [waypoints] = this.extractWaypoints(polylines)
            const geometry = this.concatenatePolylines(polylines)
            response = {
              code: "Ok",
              routes: [
                {
                  geometry,
                  legs: [],
                },
              ],
              waypoints,
            }
          } catch (error) {
            console.error("Route callback failed:", error)
            throw error
          } finally {
            this.fire(
              new MapLibreGlDirectionsRoutingEvent(
                "fetchroutesend",
                originalEvent,
                response,
              ),
            )
          }
          return response
        }),
      )

      // Filter out any undefined responses (from empty polylines)
      const validResponses = responses.filter(Boolean) as Directions[]

      // Only proceed if we have valid responses
      if (validResponses.length > 0) {
        const features = validResponses.flatMap((response, index) => {
          const profile = profiles[index]

          const snappoints = response.waypoints.map((snappoint, i) =>
            this.buildPoint(snappoint.location, "SNAPPOINT", {
              profile,
              waypointProperties: waypoints[index][i]?.properties ?? {},
            }),
          )

          const routelines = this.buildRoutelines(
            this.configuration.requestOptions,
            response.routes,
            this.selectedRouteIndex,
            snappoints,
          )

          return { snappoints, routelines }
        })
        const routes = validResponses.flatMap((response) => response.routes)

        this.snappoints = features.flatMap((features) => features.snappoints)
        this.routelines = features.flatMap((features) => features.routelines)
        if (routes.length <= this.selectedRouteIndex)
          this.selectedRouteIndex = 0

        // Only draw if we have valid routes
        this.draw(false)
      }
    } else {
      this.snappoints = []
      this.routelines = []
      this.draw(false)
    }
  }

  // Runs the debounced route request and reports what it did
  private async trackedFetchDirections(
    originalEvent: MapLibreGlDirectionsWaypointEvent,
  ) {
    const willRequest =
      !!this.routeCallback &&
      this.map.isStyleLoaded() &&
      this._waypoints.length >= 2
    if (!willRequest) return this.actualFetchDirections(originalEvent)

    const requestedAt = Date.now()
    const requestedWaypoints = this.waypoints.map(
      ([lng, lat]) => [lng, lat] as [number, number],
    )
    const routelinesBefore = this.routelines
    let outcome: "drawn" | "empty" | "error" = "error"
    try {
      await this.actualFetchDirections(originalEvent)
      // routelines are only reassigned when a fresh route gets drawn
      outcome = this.routelines !== routelinesBefore ? "drawn" : "empty"
    } finally {
      this.trackAdHocRouteRequest(
        originalEvent,
        requestedWaypoints,
        requestedAt,
        outcome,
      )
    }
  }

  private trackAdHocRouteRequest(
    originalEvent: MapLibreGlDirectionsWaypointEvent,
    waypoints: [number, number][],
    requestedAt: number,
    outcome: "drawn" | "empty" | "error",
  ) {
    const stored = this.storedRouteWaypoints
    const waypointsMatchStoredRoute = this.waypointsMatchStoredRoute(waypoints)
    const lastDrag = this.lastDrag
    const dragInProgress = this.activeDrag !== null
    const waypointCountWhenQueued = this.waypointCountWhenQueued
    trackRouteEvent("MapAdHocRouteRequest", {
      routeJid: this.routeJid,
      trigger: originalEvent?.type ?? null,
      triggerInput:
        (originalEvent?.originalEvent as { type?: string } | undefined)?.type ??
        null,
      waypointCount: waypoints.length,
      storedWaypointCount: stored?.length ?? null,
      waypointsMatchStoredRoute,
      msSinceStoredRouteDrawn:
        this.storedRouteDrawnAt !== null
          ? requestedAt - this.storedRouteDrawnAt
          : null,
      dragInProgress,
      lastDragKind: lastDrag?.kind ?? null,
      lastDragDistancePx: lastDrag?.distancePx ?? null,
      lastDragBelowThreshold: lastDrag?.belowThreshold ?? null,
      msSinceLastDragEnd: lastDrag ? requestedAt - lastDrag.endedAt : null,
      // The #707 signature: a drag that snapped back (nothing saved) still
      // recalculated the unchanged stored waypoints and drew the result
      suspectedNoOpDrag:
        waypointsMatchStoredRoute &&
        !dragInProgress &&
        !!lastDrag?.belowThreshold,
      waypointCountWhenQueued,
      // Room switch race: clear() queued a fetch with no waypoints, and the
      // stored route was hydrated before the debounce fired
      suspectedRoomLoadRace:
        originalEvent?.type === "setwaypoints" &&
        waypointCountWhenQueued === 0 &&
        waypointsMatchStoredRoute,
      outcome,
      durationMs: Date.now() - requestedAt,
    })
  }

  // Override the fetchDirections method to use the debounced version
  protected async fetchDirections(
    originalEvent: MapLibreGlDirectionsWaypointEvent,
  ) {
    this.waypointCountWhenQueued = this._waypoints.length
    this.debouncedFetchDirections(originalEvent)
  }

  public async processDirectionsResponse(response: Directions): Promise<{
    snappoints: Feature<Point>[]
    routelines: Feature<LineString>[][]
  }> {
    // Ensure we have at least one profile
    if (!this.profiles.length) {
      this.profiles = [this.configuration.profile]
    }

    // Profiles for requests
    const profiles: string[] = []
    // Waypoints split by profile
    const waypoints: Feature<Point>[][] = []

    // Prepare data for processing, similar to fetchDirections
    this.profiles.reduce((waypointsIndex, profile, index) => {
      const isLast = index === this.profiles.length - 1
      const prevProfile = index > 0 ? this.profiles[index - 1] : undefined
      const isSameProfile = profile === prevProfile
      const waypointsEnd = isLast
        ? this._waypoints.length
        : isSameProfile
          ? waypointsIndex + 1
          : waypointsIndex + 2

      if (isSameProfile) {
        waypoints[waypoints.length - 1].push(
          ...this._waypoints.slice(waypointsIndex, waypointsEnd),
        )
      } else {
        waypoints.push(this._waypoints.slice(waypointsIndex, waypointsEnd))
        profiles.push(profile)
      }

      return waypointsEnd
    }, 0)

    // Create snappoints from the response
    const snappoints = response.waypoints.map((snappoint, i) => {
      // Find corresponding profile and waypoint
      let waypointIndex = 0
      let profileIndex = 0

      for (let p = 0; p < waypoints.length; p++) {
        if (waypointIndex + waypoints[p].length > i) {
          profileIndex = p
          break
        }
        waypointIndex += waypoints[p].length
      }

      // const localIndex = i - waypointIndex
      const profile = profiles[profileIndex]
      // const waypointArray = waypoints[profileIndex]

      // Ensure index is within bounds
      // const safeIndex = Math.min(localIndex, waypointArray.length - 1)

      return this.buildPoint(snappoint.location, "SNAPPOINT", {
        profile,
        waypointProperties: {},
      })
    })

    // Create routelines from the response
    const routelines = this.buildRoutelines(
      this.configuration.requestOptions,
      response.routes,
      this.selectedRouteIndex,
      snappoints,
    )

    return { snappoints, routelines }
  }

  public cancelFetch() {
    if (this.debouncedFetchDirections.cancel) {
      this.debouncedFetchDirections.cancel()
    }
  }

  // Drop the queued ad-hoc request and ignore any response still in flight
  private discardPendingFetch() {
    this.cancelFetch()
    this.routeGeneration++
  }

  private waypointsMatchStoredRoute(waypoints: [number, number][]): boolean {
    const stored = this.storedRouteWaypoints
    return (
      !!stored &&
      stored.length === waypoints.length &&
      stored.every(
        ([lng, lat], i) => lng === waypoints[i][0] && lat === waypoints[i][1],
      )
    )
  }

  // The library's clear() goes through setWaypoints([]), which queues a
  // debounced fetch. On a room switch the stored route usually arrives before
  // it fires, so it would recalculate the stored route with PTV and replace it.
  clear() {
    super.clear()
    this.discardPendingFetch()
  }

  public clearRoute() {
    this.clear()
  }

  /**
   * @param legTraffic HERE traffic per leg (flag `route-traffic`); without it
   *   the line is colored from the TomTom `sections`, as before
   */
  public async setInitialRoute(
    polylines: string[],
    sections: TrafficSection[],
    route: Route2g,
    legTraffic?: (TrafficRange[] | undefined)[],
  ) {
    const [waypoints, allPoints] = this.extractWaypoints(polylines)
    const congestion = legTraffic
      ? congestionFromLegTraffic(
          polylines.map((encoded) => polyline.decode(encoded).length),
          legTraffic,
        )
      : this.processTomTomTraffic(sections, allPoints)
    // The library reads one annotation per leg, indexed by the leg's points
    const legs = splitCongestionByLeg(
      congestion,
      legBoundaries(
        allPoints,
        waypoints.map(({ location }) => location),
      ),
    ).map((annotation) => ({ annotation: { congestion_numeric: annotation } }))
    const geometry = this.concatenatePolylines(polylines)
    const response = {
      code: "Ok",
      routes: [{ geometry, legs }],
      waypoints,
    }

    const { snappoints, routelines } =
      await this.processDirectionsResponse(response)

    // Create a mapping that ensures alignment between waypoints and stops
    // First waypoint is always the current location
    this._waypoints = []

    const nearbyAndFarStops = route.stops?.filter(
      (s: RouteStop) =>
        s.proximity_status !== "left" && s.proximity_status !== "skipped",
    )

    // The pin badges; the layer showing them is behind a flag (MapManager)
    const pinLabels = stopPinLabels(nearbyAndFarStops ?? [])

    // For each waypoint, find the corresponding stop or use default values
    for (let i = 0; i < waypoints.length; i++) {
      let action = "location" // Default for first waypoint
      let address = ""
      let companyName
      let driverInitials
      let stopLabel: string | undefined
      if (!this.showCurrentLocation || (this.showCurrentLocation && i > 0)) {
        // Make sure we don't go out of bounds in the stops array
        const stopIndex = i - (this.showCurrentLocation ? 1 : 0)
        if (stopIndex < nearbyAndFarStops.length) {
          action = nearbyAndFarStops[stopIndex]?.action || "other"
          address = nearbyAndFarStops[stopIndex].location.formattedAddress || ""
          companyName = nearbyAndFarStops[stopIndex].location.companyName
          driverInitials = nearbyAndFarStops[stopIndex].driverInitials
          stopLabel = pinLabels[stopIndex]
        } else {
          action = "passthrough" // Default for new waypoints without matching stops
          address = ""
        }
      }
      this._waypoints.push(
        this.buildPoint(waypoints[i].location, "WAYPOINT", {
          index: i,
          action: action,
          driverInitials: driverInitials,
          address: address,
          companyName: companyName,
          // A missing key, not an undefined one, keeps the pin off the badge layer
          ...(stopLabel ? { stopLabel } : {}),
        }),
      )
    }

    this.discardPendingFetch()
    this.snappoints = snappoints
    this.routelines = routelines
    this.storedSnappoints = snappoints
    this.storedRoutelines = routelines
    this.storedRouteWaypoints = this.waypoints.map(
      ([lng, lat]) => [lng, lat] as [number, number],
    )
    this.storedRouteDrawnAt = Date.now()
    this.draw(false)
  }

  protected onDragDown(e: MapMouseEvent | MapTouchEvent) {
    // Record waypoints before any drag operation starts
    this.waypointsBeforeDrag = [...this.waypoints]

    // Query for features at the click point BEFORE calling super
    const feature = this.map.queryRenderedFeatures(e.point, {
      layers: [
        ...this.configuration.sensitiveWaypointLayers,
        ...this.configuration.sensitiveSnappointLayers,
        ...this.configuration.sensitiveAltRoutelineLayers,
        ...this.configuration.sensitiveRoutelineLayers,
      ],
    })[0]

    // The library only starts a drag (and later calls onDragUp) when its own
    // source is the topmost feature under the pointer
    const libraryStartsDrag =
      this.map.queryRenderedFeatures(e.point)[0]?.source ===
      this.configuration.sourceName
    const layerId = feature?.layer?.id ?? ""
    const dragKind: RouteDragKind | null = !libraryStartsDrag
      ? null
      : this.configuration.sensitiveWaypointLayers.includes(layerId)
        ? "waypoint"
        : this.configuration.sensitiveRoutelineLayers.includes(layerId)
          ? "routeline"
          : null
    this.activeDrag = dragKind
      ? { kind: dragKind, startPoint: { x: e.point.x, y: e.point.y } }
      : null

    // The library compares against the position of the last live refresh,
    // which starts at (0, 0), so any wobble during a click would recalculate
    // the route. Only refresh once the pointer really moved.
    this.lastRequestMousePosition = { x: e.point.x, y: e.point.y }

    // Call the original method to maintain normal functionality
    super.onDragDown(e)

    // Check if we're dragging a route line by verifying:
    // 1. departSnappointIndex is set (indicates potential route line interaction)
    // 2. The click is actually on a route line (not just panning the map)
    const isClickOnRouteLine =
      feature &&
      this.configuration.sensitiveRoutelineLayers.includes(
        feature.layer?.id ?? "",
      )

    if (this.departSnappointIndex !== -1 && isClickOnRouteLine) {
      this.isDraggingRouteLine = true
      this.onRouteDragChange?.(true)
    }

    // Don't start drag for these action types
    if (
      feature &&
      (feature.properties.action === "location" ||
        feature.properties.action === "pin" ||
        feature.properties.action === "from_driver_location")
    ) {
      return
    }
  }

  // Override onDragUp method to capture the end of dragging
  protected onDragUp(e: MapMouseEvent | MapTouchEvent) {
    const wasDraggingRouteLine = this.isDraggingRouteLine

    if (this.activeDrag) {
      const distancePx = Math.max(
        Math.abs(e.point.x - this.activeDrag.startPoint.x),
        Math.abs(e.point.y - this.activeDrag.startPoint.y),
      )
      this.lastDrag = {
        kind: this.activeDrag.kind,
        endedAt: Date.now(),
        distancePx: Math.round(distancePx),
        belowThreshold:
          distancePx <= Math.max(this.configuration.dragThreshold, 0),
      }
      this.activeDrag = null
    }

    // Call the original method first to let it process the drag end
    super.onDragUp(e)

    // A waypoint drag that ended within the threshold snaps back and saves
    // nothing, so drop its live preview and show the stored route again
    if (
      this.lastDrag?.kind === "waypoint" &&
      this.lastDrag.belowThreshold &&
      this.waypointsMatchStoredRoute(this.waypoints)
    ) {
      this.discardPendingFetch()
      if (
        this.storedRoutelines &&
        this.storedSnappoints &&
        this.routelines !== this.storedRoutelines
      ) {
        this.snappoints = this.storedSnappoints
        this.routelines = this.storedRoutelines
        this.draw(false)
      }
    }

    // If we were dragging a route line
    if (wasDraggingRouteLine) {
      this.isDraggingRouteLine = false
      this.onRouteDragChange?.(false)

      // Compare waypoints before and after drag
      const waypointsAfterDrag = this.waypoints

      if (waypointsAfterDrag.length > this.waypointsBeforeDrag.length) {
        // Find the new waypoint (there should be exactly one more)
        const newIndex = this.findNewWaypointIndex(
          this.waypointsBeforeDrag,
          waypointsAfterDrag,
        )

        if (newIndex !== -1) {
          // Fire a custom event to notify about the new waypoint
          const customEvent = new CustomEvent<RouteLineDragEndEventDetail>(
            "routelinedragend",
            {
              detail: {
                waypointIndex: newIndex,
                waypoint: waypointsAfterDrag[newIndex],
              },
            },
          )

          // Dispatch the event on the map container element
          this.map.getContainer().dispatchEvent(customEvent)
        }
      } else {
        // Route line drag ended without adding waypoint
      }
    }
  }

  // Helper method to find the index of the new waypoint
  private findNewWaypointIndex(
    oldWaypoints: [number, number][],
    newWaypoints: [number, number][],
  ): number {
    // Simple case: if only one waypoint was added, it should be at the end
    if (oldWaypoints.length + 1 === newWaypoints.length) {
      // Check if a waypoint was added in the middle
      for (let i = 0; i < newWaypoints.length; i++) {
        // If we've reached the end of old waypoints, the new one must be at the end
        if (i >= oldWaypoints.length) return i

        // If the coordinates don't match, this must be the new waypoint
        const oldCoord = oldWaypoints[i]
        const newCoord = newWaypoints[i]
        if (oldCoord[0] !== newCoord[0] || oldCoord[1] !== newCoord[1]) {
          return i
        }
      }
    }
    return -1 // No new waypoint found
  }

  // Helper method to find the index of the changed waypoint
  private findChangedWaypointIndex(
    oldWaypoints: [number, number][],
    newWaypoints: [number, number][],
  ): number {
    // Only compare if arrays have the same length (modification, not addition/removal)
    if (oldWaypoints.length === newWaypoints.length) {
      for (let i = 0; i < newWaypoints.length; i++) {
        const oldCoord = oldWaypoints[i]
        const newCoord = newWaypoints[i]

        // If the coordinates don't match, this waypoint was changed
        if (oldCoord[0] !== newCoord[0] || oldCoord[1] !== newCoord[1]) {
          return i
        }
      }
    }
    return -1 // No changed waypoint found
  }

  // Override the onClick method to prevent adding waypoints when clicking on the map
  protected onClick(e: MapMouseEvent | MapTouchEvent) {
    const feature: MapGeoJSONFeature | undefined =
      this.map.queryRenderedFeatures(e.point, {
        layers: [
          ...this.configuration.sensitiveWaypointLayers,
          ...this.configuration.sensitiveSnappointLayers,
          ...this.configuration.sensitiveAltRoutelineLayers,
          ...this.configuration.sensitiveRoutelineLayers,
        ],
      })[0]

    if (
      this.interactive &&
      this.configuration.sensitiveWaypointLayers.includes(
        feature?.layer.id ?? "",
      )
    ) {
      // Only remove waypoint if it has action='passthrough'
      const respectiveWaypointIndex = this._waypoints.findIndex((waypoint) => {
        return waypoint.properties?.id === feature?.properties?.id
      })

      if (~respectiveWaypointIndex) {
        // const waypoint = this._waypoints[respectiveWaypointIndex]
        // Only remove if the waypoint has action='passthrough'
        // if (waypoint.properties?.action === "passthrough") {
        //   this._removeWaypoint(respectiveWaypointIndex, e)
        // }
      }
    } else if (
      this.interactive &&
      this.configuration.sensitiveSnappointLayers.includes(
        feature?.layer.id ?? "",
      )
    ) {
      // Find the related waypoint for this snappoint
      const respectiveWaypointIndex = this.snappoints.findIndex((snappoint) => {
        return snappoint.properties?.id === feature?.properties?.id
      })

      if (~respectiveWaypointIndex) {
        // const waypoint = this._waypoints[respectiveWaypointIndex]
        // Only remove if the associated waypoint has action='passthrough'
        // if (waypoint.properties?.action === "passthrough") {
        //   this._removeWaypoint(respectiveWaypointIndex, e)
        // }
      }
    } else if (
      (this.interactive || this.allowRouteSwitch) &&
      this.configuration.sensitiveAltRoutelineLayers.includes(
        feature?.layer.id ?? "",
      )
    ) {
      // If an alternative routeline is clicked, set its index as the selected route's one.
      this.selectedRouteIndex = this.routelines.findIndex((routeline) => {
        return !!routeline.find((segment) => {
          return segment.properties?.id === feature?.properties?.id
        })
      })
    }
    // Remove the case that adds a waypoint when clicking on empty map space
    // We're intentionally not calling this._addWaypoint here

    // The selected route index might have changed, so it's important not to skip its redraw.
    this.draw(false)

    // After the waypoint interaction, imitate hovering the mouse over it
    this.map.once("idle", () => {
      this.onMove(e)
    })
  }

  /**
   * Gets coordinates of a point feature
   */
  private getWaypointsCoordinates(
    waypoints: Feature<Point>[],
  ): [number, number][] {
    return waypoints.map((waypoint) => {
      return [
        waypoint.geometry.coordinates[0],
        waypoint.geometry.coordinates[1],
      ]
    })
  }

  /**
   * Gets bearings out of properties of point features
   */
  private getWaypointsBearings(
    waypoints: Feature<Point>[],
  ): ([number, number] | undefined)[] {
    return waypoints.map((waypoint) => {
      return Array.isArray(waypoint.properties?.bearing)
        ? [waypoint.properties?.bearing[0], waypoint.properties?.bearing[1]]
        : undefined
    })
  }

  public concatenatePolylines(encodedPolylines: string[]): string {
    // Step 1: Decode each polyline to get the coordinates
    const decodedPolylines = encodedPolylines.map((encoded) =>
      polyline.decode(encoded),
    )

    // Step 2: Concatenate all coordinate arrays
    let combinedCoordinates: [number, number][] = []
    for (const coords of decodedPolylines) {
      combinedCoordinates = combinedCoordinates.concat(coords)
    }

    // Step 3: Re-encode the combined coordinates
    return polyline.encode(combinedCoordinates)
  }

  public extractWaypoints(
    encodedPolylines: string[],
  ): [{ location: [number, number] }[], [number, number][]] {
    if (!encodedPolylines.length) {
      throw new Error("No polylines provided")
    }
    const allPoints: [number, number][] = []
    const waypoints: { location: [number, number] }[] = []

    // Add the first point of each polyline as a waypoint
    for (const encoded of encodedPolylines) {
      const coordinates = polyline.decode(encoded)
      if (coordinates.length === 0) {
        throw new Error("Empty polyline encountered")
      }
      const convertedCoordinates: [number, number][] = coordinates.map(
        ([lat, lng]) => [lng, lat],
      )
      allPoints.push(...convertedCoordinates)

      waypoints.push({ location: [coordinates[0][1], coordinates[0][0]] })
    }

    // Add the last point of the last polyline as the final waypoint
    const lastPolyline = polyline.decode(
      encodedPolylines[encodedPolylines.length - 1],
    )
    const lastPoint = lastPolyline[lastPolyline.length - 1]
    waypoints.push({ location: [lastPoint[1], lastPoint[0]] })

    return [waypoints, allPoints]
  }

  /**
   * Finds visible segments of a route and returns the center point of the longest visible segment
   * @param encodedPolylines Array of encoded polylines representing the route
   * @param map The maplibre-gl Map instance to check visibility against
   * @returns The center coordinate of the longest visible segment, or the middle point if no segments are visible
   */
  public findVisibleRouteCenter(encodedPolylines: string[]): ILngLatLike {
    if (!encodedPolylines.length) {
      return [0, 0]
    }

    // Decode all polylines while tracking waypoints (start/end of each polyline)
    const waypointIndices = new Set<number>()
    let currentIndex = 0
    const allPoints: [number, number][] = []

    // Process each polyline and track waypoints
    encodedPolylines.forEach((encoded) => {
      const points = polyline.decode(encoded)
      if (points.length > 0) {
        // Mark start point
        waypointIndices.add(currentIndex)

        // Add all points from this polyline
        points.forEach((point) => {
          allPoints.push(point)
          currentIndex++
        })

        // Mark end point (last added point)
        waypointIndices.add(currentIndex - 1)
      }
    })

    if (!allPoints.length) {
      return [0, 0]
    }

    const visibleBounds = this.map.getBounds()
    let longestVisibleSegment: { start: number; end: number; length: number } =
      {
        start: 0,
        end: 0,
        length: 0,
      }

    let currentSegmentStart = -1

    // Find all visible segments of the route
    for (let i = 0; i < allPoints.length; i++) {
      const point = allPoints[i]
      const isVisible = visibleBounds.contains([point[1], point[0]])

      if (isVisible && currentSegmentStart === -1) {
        // Start of a new visible segment
        currentSegmentStart = i
      } else if (!isVisible && currentSegmentStart !== -1) {
        // End of a visible segment
        const segmentLength = i - currentSegmentStart
        if (segmentLength > longestVisibleSegment.length) {
          longestVisibleSegment = {
            start: currentSegmentStart,
            end: i - 1,
            length: segmentLength,
          }
        }
        currentSegmentStart = -1
      }
    }

    // Check if the last segment extends to the end
    if (currentSegmentStart !== -1) {
      const segmentLength = allPoints.length - currentSegmentStart
      if (segmentLength > longestVisibleSegment.length) {
        longestVisibleSegment = {
          start: currentSegmentStart,
          end: allPoints.length - 1,
          length: segmentLength,
        }
      }
    }

    // If we found a visible segment, find a suitable point that's not a waypoint
    if (longestVisibleSegment.length > 0) {
      // Find a safe point within the longest visible segment
      for (
        let i = longestVisibleSegment.start;
        i <= longestVisibleSegment.end;
        i++
      ) {
        // Target middle area of segment, working outward
        const middleIndex = Math.floor(
          (longestVisibleSegment.start + longestVisibleSegment.end) / 2,
        )
        const halfLength = Math.floor(longestVisibleSegment.length / 2)

        // Try positions around the middle, alternating sides to find a non-waypoint
        for (let offset = 0; offset <= halfLength; offset++) {
          // Check middle + offset
          const indexRight = middleIndex + offset
          if (
            indexRight <= longestVisibleSegment.end &&
            !waypointIndices.has(indexRight)
          ) {
            return [allPoints[indexRight][1], allPoints[indexRight][0]]
          }

          // Check middle - offset
          const indexLeft = middleIndex - offset
          if (
            indexLeft >= longestVisibleSegment.start &&
            !waypointIndices.has(indexLeft)
          ) {
            return [allPoints[indexLeft][1], allPoints[indexLeft][0]]
          }
        }
      }

      // Fallback if all points in segment are waypoints (unlikely)
      const midIndex = Math.floor(
        longestVisibleSegment.start +
          (longestVisibleSegment.end - longestVisibleSegment.start) / 2,
      )
      return [allPoints[midIndex][1], allPoints[midIndex][0]]
    }

    // Fallback: Find any non-waypoint in the middle third of the route
    const totalLength = allPoints.length
    const startIndex = Math.floor(totalLength / 3)
    const endIndex = Math.floor((2 * totalLength) / 3)

    for (let i = startIndex; i <= endIndex; i++) {
      if (!waypointIndices.has(i)) {
        return [allPoints[i][1], allPoints[i][0]]
      }
    }

    // Final fallback - middle point of entire route
    const finalIndex = Math.floor(totalLength / 2)
    return [allPoints[finalIndex][1], allPoints[finalIndex][0]]
  }

  get waypointsFeatures() {
    return this._waypoints
  }

  setWaypointsFeatures(waypointsFeatures: Feature<Point>[]) {
    this._waypoints = waypointsFeatures

    this.assignWaypointsCategories()

    const waypointEvent = new MapLibreGlDirectionsWaypointEvent(
      "setwaypoints",
      undefined,
    )
    this.fire(waypointEvent)

    this.draw()
  }

  get snappointsFeatures() {
    return this.snappoints
  }

  setSnappointsFeatures(snappointsFeatures: Feature<Point>[]) {
    this.snappoints = snappointsFeatures
    this.draw()
  }

  get routelinesFeatures() {
    return this.routelines
  }

  setRoutelinesFeatures(routelinesFeatures: Feature<LineString>[][]) {
    this.routelines = routelinesFeatures
    this.draw()
  }
}
